import React, { useState, useEffect } from 'react';
import { Filter, X, Calendar, Users, Building, Briefcase, RefreshCw, Bookmark, Plus, Trash2, ArrowRight, ChevronDown } from 'lucide-react';
import FilterDropdown from './FilterDropdown';
import { toast } from 'react-hot-toast';
import api from '../../utils/api';

const FilterBar = ({
    filters = {},
    options = {},
    onChange,
    onReset,
    onRefresh,
    loading = false,
    className = ''
}) => {
    const [savedFilters, setSavedFilters] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [saveName, setSaveName] = useState('');
    const [showSaveMenu, setShowSaveMenu] = useState(false);
    const [loadingSaved, setLoadingSaved] = useState(false);

    // Fetch saved filters on mount
    useEffect(() => {
        fetchSavedFilters();
    }, []);

    const fetchSavedFilters = async () => {
        try {
            setLoadingSaved(true);
            const res = await api.get('/analytics/saved-filters');
            if (res.data?.success) {
                setSavedFilters(res.data.savedFilters || []);
            }
        } catch (error) {
            console.error("Failed to fetch saved filters", error);
        } finally {
            setLoadingSaved(false);
        }
    };

    const handleSaveFilter = async () => {
        if (!saveName.trim()) return toast.error("Please enter a name");
        try {
            setIsSaving(true);
            const res = await api.post('/analytics/saved-filters', {
                name: saveName.trim(),
                filters: filters
            });
            if (res.data?.success) {
                toast.success("Filter saved");
                setSavedFilters([res.data.savedFilter, ...savedFilters]);
                setSaveName('');
                setShowSaveMenu(false);
            }
        } catch (error) {
            toast.error("Failed to save filter");
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteSaved = async (id, e) => {
        e.stopPropagation();
        try {
            const res = await api.delete(`/analytics/saved-filters/${id}`);
            if (res.data?.success) {
                setSavedFilters(savedFilters.filter(f => f.id !== id));
                toast.success("Deleted saved filter");
            }
        } catch (error) {
            toast.error("Failed to delete filter");
        }
    };

    // Helper to translate from backend 'all'/'1,2' format to arrays for UI Component
    const parseValue = (val) => {
        if (!val || val === 'all') return [];
        return val.toString().split(',');
    };

    // Helper to translate from arrays back to 'all'/'1,2' for parent state
    const handleChange = (key, arrOrStr) => {
        if (Array.isArray(arrOrStr)) {
            onChange({ ...filters, [key]: arrOrStr.length === 0 ? 'all' : arrOrStr.join(',') });
        } else {
            onChange({ ...filters, [key]: arrOrStr }); // for date range select
        }
    };

    // Remove single item from comma separated string
    const removeFilterItem = (key, itemValueToRemove) => {
        const currentArr = parseValue(filters[key]);
        const newArr = currentArr.filter(v => v !== itemValueToRemove.toString());
        handleChange(key, newArr);
    };

    const hasActiveFilters = Object.values(filters).some(v => v && v !== 'all' && v !== '30'); // '30' is default dateRange

    // Build filter chips
    const renderActiveChips = () => {
        const chips = [];

        // Teams
        if (filters.teamId && filters.teamId !== 'all') {
            parseValue(filters.teamId).forEach(id => {
                const opt = options.teams?.find(t => t.id.toString() === id);
                if (opt) {
                    chips.push(
                        <span key={`team-${id}`} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-primary/10 text-primary rounded-full">
                            Team: {opt.team_name}
                            <button onClick={() => removeFilterItem('teamId', id)} className="hover:text-primary-dark transition-colors"><X size={12} /></button>
                        </span>
                    );
                }
            });
        }

        // Departments
        if (filters.department && filters.department !== 'all') {
            parseValue(filters.department).forEach(dept => {
                chips.push(
                    <span key={`dept-${dept}`} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                        Dept: {dept}
                        <button onClick={() => removeFilterItem('department', dept)} className="hover:text-blue-900 transition-colors"><X size={12} /></button>
                    </span>
                );
            });
        }

        // Positions
        if (filters.position && filters.position !== 'all') {
            parseValue(filters.position).forEach(pos => {
                chips.push(
                    <span key={`pos-${pos}`} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-emerald-100 text-emerald-700 rounded-full">
                        Role: {pos}
                        <button onClick={() => removeFilterItem('position', pos)} className="hover:text-emerald-900 transition-colors"><X size={12} /></button>
                    </span>
                );
            });
        }

        // Generic mappers for simple arrays
        const mapSimpleArray = (key, label, colorCls) => {
            if (filters[key] && filters[key] !== 'all') {
                parseValue(filters[key]).forEach(val => {
                    chips.push(
                        <span key={`${key}-${val}`} className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${colorCls}`}>
                            {label}: {val}
                            <button onClick={() => removeFilterItem(key, val)} className="hover:opacity-75 transition-opacity"><X size={12} /></button>
                        </span>
                    );
                });
            }
        };

        mapSimpleArray('riskLevel', 'Risk', 'bg-red-100 text-red-700');
        mapSimpleArray('taskType', 'Type', 'bg-amber-100 text-amber-700');
        mapSimpleArray('status', 'Status', 'bg-blue-100 text-blue-700');

        return chips.length > 0 ? (
            <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-gray-100">
                <span className="text-sm font-medium text-gray-400 mr-1">Active:</span>
                {chips}
            </div>
        ) : null;
    };

    return (
        <div className={`bg-white rounded-xl border border-gray-200 p-4 shadow-sm ${className}`}>
            <div className="flex flex-wrap items-center gap-3">

                {/* Saved Filters Dropdown */}
                <div className="relative group">
                    <button
                        className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                        onClick={() => setShowSaveMenu(!showSaveMenu)}
                    >
                        <Bookmark className="w-4 h-4 text-blue-500" />
                        Saved Filters
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                    </button>

                    {showSaveMenu && (
                        <div className="absolute top-full left-0 mt-2 w-64 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden">
                            <div className="p-3 bg-gray-50 border-b border-gray-100">
                                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Save Current</div>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        placeholder="Preset name..."
                                        className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                                        value={saveName}
                                        onChange={e => setSaveName(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && handleSaveFilter()}
                                    />
                                    <button
                                        onClick={handleSaveFilter}
                                        disabled={isSaving || !saveName.trim()}
                                        className="px-2.5 py-1.5 bg-primary text-white rounded text-sm font-medium hover:bg-primary-dark disabled:opacity-50 transition-colors"
                                    >
                                        Save
                                    </button>
                                </div>
                            </div>

                            <div className="max-h-60 overflow-y-auto">
                                {loadingSaved ? (
                                    <div className="p-4 text-center text-sm text-gray-400">Loading...</div>
                                ) : savedFilters.length === 0 ? (
                                    <div className="p-4 text-center text-sm text-gray-400 italic">No saved filters yet</div>
                                ) : (
                                    savedFilters.map(sf => (
                                        <div
                                            key={sf.id}
                                            className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 cursor-pointer group"
                                            onClick={() => { onChange(sf.filters); setShowSaveMenu(false); }}
                                        >
                                            <span className="text-sm font-medium text-gray-700 group-hover:text-primary transition-colors">{sf.name}</span>
                                            <div className="flex items-center gap-1">
                                                <button
                                                    onClick={(e) => handleDeleteSaved(sf.id, e)}
                                                    className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors"
                                                    title="Delete preset"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                                <ArrowRight size={14} className="text-gray-300 group-hover:text-primary transition-colors ml-1" />
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <div className="h-6 w-px bg-gray-200 mx-1"></div>

                {/* Date range dropdown (retained as single select) */}
                {options.showDateRange && (
                    <div className="flex items-center gap-2">
                        <select
                            value={filters.dateRange || '30'}
                            onChange={(e) => handleChange('dateRange', e.target.value)}
                            className="bg-gray-50 text-sm border-0 font-medium text-gray-700 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-primary/20 hover:bg-gray-100 transition-colors outline-none cursor-pointer"
                        >
                            <option value="7">Last 7 days</option>
                            <option value="30">Last 30 days</option>
                            <option value="90">Last 90 days</option>
                            <option value="365">Last year</option>
                            <option value="all">All time</option>
                        </select>
                    </div>
                )}

                {/* Team Multi-select */}
                {options.teams && options.teams.length > 0 && (
                    <FilterDropdown
                        label="Teams"
                        icon={Users}
                        options={options.teams.map(t => ({ value: t.id.toString(), label: t.team_name }))}
                        selectedValues={parseValue(filters.teamId)}
                        onChange={arr => handleChange('teamId', arr)}
                    />
                )}

                {/* Department Multi-select */}
                {options.departments && options.departments.length > 0 && (
                    <FilterDropdown
                        label="Departments"
                        icon={Building}
                        options={options.departments}
                        selectedValues={parseValue(filters.department)}
                        onChange={arr => handleChange('department', arr)}
                    />
                )}

                {/* Position/Role Multi-select */}
                {options.positions && options.positions.length > 0 && (
                    <FilterDropdown
                        label="Roles"
                        icon={Briefcase}
                        options={options.positions}
                        selectedValues={parseValue(filters.position)}
                        onChange={arr => handleChange('position', arr)}
                    />
                )}

                {/* Risk Level Multi-select */}
                {options.showRiskLevel && (
                    <FilterDropdown
                        label="Risk Level"
                        options={[{ value: 'high', label: 'High Risk' }, { value: 'medium', label: 'Medium Risk' }, { value: 'low', label: 'Low Risk' }]}
                        selectedValues={parseValue(filters.riskLevel)}
                        onChange={arr => handleChange('riskLevel', arr)}
                    />
                )}

                {/* Task Type Multi-select */}
                {options.showTaskType && (
                    <FilterDropdown
                        label="Task Type"
                        options={[{ value: 'task', label: 'Tasks' }, { value: 'quiz', label: 'Quizzes' }, { value: 'training', label: 'Training' }, { value: 'checklist', label: 'Checklists' }]}
                        selectedValues={parseValue(filters.taskType)}
                        onChange={arr => handleChange('taskType', arr)}
                    />
                )}

                {/* Status Multi-select */}
                {options.showStatus && (
                    <FilterDropdown
                        label="Status"
                        options={[{ value: 'completed', label: 'Completed' }, { value: 'in_progress', label: 'In Progress' }, { value: 'pending', label: 'Pending' }, { value: 'overdue', label: 'Overdue' }]}
                        selectedValues={parseValue(filters.status)}
                        onChange={arr => handleChange('status', arr)}
                    />
                )}

                {/* Spacer */}
                <div className="flex-1" />

                {/* Action Buttons */}
                <div className="flex items-center gap-2">
                    {hasActiveFilters && (
                        <button
                            onClick={onReset}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100"
                        >
                            <X className="w-4 h-4" />
                            Reset
                        </button>
                    )}

                    {onRefresh && (
                        <button
                            onClick={onRefresh}
                            disabled={loading}
                            className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors disabled:opacity-50"
                        >
                            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                            <span className="hidden sm:inline">Refresh</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Active Constraints / Filter Chips */}
            {renderActiveChips()}

        </div>
    );
};

export default FilterBar;
